import { ReactNode, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { motion } from 'framer-motion'

import {
  ErrorMessage,
  GreyWhite,
  ShadowL,
  Spinner,
  Center,
  Box
} from '@sequencehq/core-components'
import { useManualTask } from '../hooks/useManualTask'
import { manualReviewTasks } from 'Automations/nodeDefinitions/nodeDefinitions'
import { useAtomValue, useSetAtom } from 'jotai'
import { manualTaskProcessorTaskIdAtom } from 'Automations/watchtower/atoms'
import { useStandardLayoutModalPortalRoot } from 'components/Layout/StandardLayoutModalPortalRoot'

const FullScreenModalLayout = (props: {
  children: ReactNode
  'data-testid'?: string
}) => {
  const portalTarget = useStandardLayoutModalPortalRoot()

  // Wait for the portal target to mount; falling back to document.body
  // causes a 0×0 collapse because `<div id="root">` is position: fixed.
  if (!portalTarget) return null

  return createPortal(
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
        style={{
          position: 'absolute',
          inset: 0,
          // Above the navbar (zIndex 2) so the modal covers it.
          zIndex: 35,
          backgroundColor: 'rgba(0, 0, 0, 0.16)'
        }}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 35,
          transformOrigin: 'center'
        }}
      >
        <Box
          position="absolute"
          top="8px"
          left="8px"
          right="8px"
          bottom="8px"
          borderRadius="8px"
          outline="1px solid rgba(0, 0, 0, 0.08)"
          boxShadow={ShadowL}
          overflow="hidden"
          background={GreyWhite}
          data-testid={props['data-testid']}
        >
          {props.children}
        </Box>
      </motion.div>
    </>,
    portalTarget
  )
}

export const ManualTaskProcessor = () => {
  const taskId = useAtomValue(manualTaskProcessorTaskIdAtom)
  const setProcessorTaskId = useSetAtom(manualTaskProcessorTaskIdAtom)
  const { manualTask: selectedManualTask, isPending } = useManualTask({
    taskId: taskId ?? null,
    poll: true
  })

  // Close the modal once the task is no longer actionable. Completing via
  // approve/reject already clears the atom; this handles external cancellation
  // or completion
  const taskStatus = selectedManualTask?.status
  const isTaskResolved = Boolean(taskStatus && taskStatus !== 'PENDING')
  useEffect(() => {
    if (isTaskResolved) {
      setProcessorTaskId(null)
    }
  }, [isTaskResolved, setProcessorTaskId])

  const ManualTaskProcessorComponent = useMemo(() => {
    if (!selectedManualTask || !selectedManualTask.type) {
      return null
    }

    return manualReviewTasks[selectedManualTask.type]?.reviewContent ?? null
  }, [selectedManualTask])

  if (isPending) {
    return (
      <FullScreenModalLayout>
        <Center height="100%" width="100%">
          <Spinner />
        </Center>
      </FullScreenModalLayout>
    )
  }

  if (!ManualTaskProcessorComponent || !selectedManualTask) {
    return (
      <FullScreenModalLayout>
        <ErrorMessage title="We couldn't load your task" />
      </FullScreenModalLayout>
    )
  }

  return (
    <FullScreenModalLayout
      data-testid={`watchtower.manualTaskProcessor.${selectedManualTask.id}`}
    >
      <ManualTaskProcessorComponent taskId={selectedManualTask.id} />
    </FullScreenModalLayout>
  )
}
